import React, { useState, useEffect } from 'react';
import { Users, Activity, Calendar, Droplet, Heart, AlertTriangle, BarChart2, TrendingUp, Clock, Layers, 
  FileText, Hospital, Truck, Gift, Database, Shield, Settings, ArrowUp, ArrowDown, Zap } from 'lucide-react';
import api from '../../../config/api';
import { toast } from 'react-hot-toast';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalDonors: 0,
    activeDonors: 0,
    pendingRequests: 0,
    completedDonations: 0,
    emergencyRequests: 0,
    totalBloodUnits: 0,
    bloodInventory: {
      'A+': 0, 'A-': 0, 'B+': 0, 'B-': 0, 
      'AB+': 0, 'AB-': 0, 'O+': 0, 'O-': 0
    },
    recentActivity: []
  });
  const [loading, setLoading] = useState(true);

  // Blood type colors for visualization
  const bloodTypeColors = {
    'A+': '#ef4444',
    'A-': '#f87171',
    'B+': '#3b82f6',
    'B-': '#60a5fa',
    'AB+': '#8b5cf6',
    'AB-': '#a78bfa',
    'O+': '#10b981',
    'O-': '#34d399'
  };

  // Fetch dashboard statistics
  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        setLoading(true);
        const response = await api.get('/admin/dashboard/stats');
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        toast.error('Failed to load dashboard statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, []);

  // Loading skeleton
  const StatCardSkeleton = () => (
    <div className="bg-white rounded-xl shadow-md p-6 animate-pulse">
      <div className="h-6 w-32 bg-gray-200 rounded mb-4"></div>
      <div className="h-10 w-20 bg-gray-300 rounded mb-3"></div>
      <div className="h-4 w-40 bg-gray-200 rounded"></div>
    </div>
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Admin Dashboard</h1>
        <p className="text-gray-600 mt-1">Comprehensive overview of the blood donation system</p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Donors Card */}
        {loading ? (
          <StatCardSkeleton />
        ) : (
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500 hover:shadow-lg transition-shadow">
            <div className="flex justify-between">
              <div>
                <h3 className="text-gray-500 text-sm font-medium">Total Donors</h3>
                <div className="flex items-end mt-2">
                  <span className="text-3xl font-bold text-gray-800">{stats.totalDonors}</span>
                  <span className="text-xs text-green-600 ml-2 mb-1 flex items-center">
                    <ArrowUp className="w-3 h-3 mr-1" />
                    12%
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-2">Registered blood donors</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
        )}

        {/* Active Donors Card */}
        {loading ? (
          <StatCardSkeleton />
        ) : (
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500 hover:shadow-lg transition-shadow">
            <div className="flex justify-between">
              <div>
                <h3 className="text-gray-500 text-sm font-medium">Active Donors</h3>
                <div className="flex items-end mt-2">
                  <span className="text-3xl font-bold text-gray-800">{stats.activeDonors}</span>
                  <span className="text-xs text-green-600 ml-2 mb-1 flex items-center">
                    <ArrowUp className="w-3 h-3 mr-1" />
                    8%
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-2">Donated in last 3 months</p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <Activity className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
        )}

        {/* Pending Requests Card */}
        {loading ? (
          <StatCardSkeleton />
        ) : (
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-yellow-500 hover:shadow-lg transition-shadow">
            <div className="flex justify-between">
              <div>
                <h3 className="text-gray-500 text-sm font-medium">Pending Requests</h3>
                <div className="flex items-end mt-2">
                  <span className="text-3xl font-bold text-gray-800">{stats.pendingRequests}</span>
                  <span className="text-xs text-yellow-600 ml-2 mb-1 flex items-center">
                    <Clock className="w-3 h-3 mr-1" />
                    Awaiting
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-2">Require approval</p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-lg">
                <FileText className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>
        )}

        {/* Emergency Requests Card */}
        {loading ? (
          <StatCardSkeleton />
        ) : (
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-red-500 hover:shadow-lg transition-shadow">
            <div className="flex justify-between">
              <div>
                <h3 className="text-gray-500 text-sm font-medium">Emergency Requests</h3>
                <div className="flex items-end mt-2">
                  <span className="text-3xl font-bold text-gray-800">{stats.emergencyRequests}</span>
                  <span className="text-xs text-red-600 ml-2 mb-1 flex items-center">
                    <Zap className="w-3 h-3 mr-1" />
                    Urgent
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-2">High priority needs</p>
              </div>
              <div className="bg-red-100 p-3 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Blood Inventory and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Blood Inventory */}
        {loading ? (
          <div className="bg-white rounded-xl shadow-md p-6 animate-pulse">
            <div className="h-6 w-40 bg-gray-200 rounded mb-6"></div>
            <div className="grid grid-cols-4 gap-4">
              {Array(8).fill(0).map((_, i) => (
                <div key={i} className="flex flex-col items-center">
                  <div className="w-16 h-16 rounded-full bg-gray-200"></div>
                  <div className="h-4 w-12 bg-gray-200 rounded mt-2"></div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-gray-800">Blood Inventory</h2>
              <div className="text-sm text-gray-500 flex items-center">
                <Database className="w-4 h-4 mr-1" />
                <span>Total: {stats.totalBloodUnits} units</span>
              </div>
            </div>
            
            <div className="grid grid-cols-4 gap-6">
              {Object.entries(stats.bloodInventory).map(([type, count]) => (
                <div key={type} className="flex flex-col items-center">
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold shadow-md"
                    style={{ backgroundColor: bloodTypeColors[type] }}
                  >
                    {type}
                  </div>
                  <div className="mt-2 text-center">
                    <span className="font-semibold text-gray-700">{count}</span>
                    <span className="text-xs text-gray-500 block">units</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Activity */}
        {loading ? (
          <div className="bg-white rounded-xl shadow-md p-6 animate-pulse">
            <div className="h-6 w-40 bg-gray-200 rounded mb-6"></div>
            <div className="space-y-4">
              {Array(4).fill(0).map((_, i) => (
                <div key={i} className="flex p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-gray-200 mr-3"></div>
                  <div className="flex-1">
                    <div className="h-4 w-full bg-gray-200 rounded mb-2"></div>
                    <div className="h-3 w-3/4 bg-gray-200 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-gray-800">Recent Activity</h2>
              <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">View All</button>
            </div>
            
            <div className="space-y-4">
              {stats.recentActivity && stats.recentActivity.length > 0 ? (
                stats.recentActivity.map((activity, index) => (
                  <div key={index} className="flex p-3 bg-gray-50 rounded-lg">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                      activity.type === 'Donation' 
                        ? 'bg-green-100 text-green-600' 
                        : 'bg-blue-100 text-blue-600'
                    }`}>
                      {activity.type === 'Donation' ? <Droplet className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-800">{activity.description}</p>
                      <p className="text-xs text-gray-500">{activity.date}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full self-start ${activity.statusClass}`}>
                      {activity.status}
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-center py-6 text-gray-500">
                  No recent activity
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* System Status and Quick Links */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* System Status */}
        <div className="bg-white rounded-xl shadow-md p-6 lg:col-span-1">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">System Status</h2>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                <span className="text-sm text-gray-700">API Services</span>
              </div>
              <span className="text-xs text-green-600 font-medium">Operational</span>
            </div>
            
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                <span className="text-sm text-gray-700">Database</span>
              </div>
              <span className="text-xs text-green-600 font-medium">Operational</span>
            </div>
            
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                <span className="text-sm text-gray-700">Notification System</span>
              </div>
              <span className="text-xs text-green-600 font-medium">Operational</span>
            </div>
            
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                <span className="text-sm text-gray-700">WebSocket Server</span>
              </div>
              <span className="text-xs text-green-600 font-medium">Operational</span>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="bg-white rounded-xl shadow-md p-6 lg:col-span-2">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h2>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <button className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <Users className="w-6 h-6 text-blue-600 mb-2" />
              <span className="text-sm text-gray-700">Manage Donors</span>
            </button>
            
            <button className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <FileText className="w-6 h-6 text-yellow-600 mb-2" />
              <span className="text-sm text-gray-700">Review Requests</span>
            </button>
            
            <button className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <Hospital className="w-6 h-6 text-red-600 mb-2" />
              <span className="text-sm text-gray-700">Manage Hospitals</span>
            </button>
            
            <button className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <Calendar className="w-6 h-6 text-purple-600 mb-2" />
              <span className="text-sm text-gray-700">Schedule Events</span>
            </button>
            
            <button className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <Database className="w-6 h-6 text-green-600 mb-2" />
              <span className="text-sm text-gray-700">Blood Inventory</span>
            </button>
            
            <button className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <Settings className="w-6 h-6 text-gray-600 mb-2" />
              <span className="text-sm text-gray-700">System Settings</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;